import {
  Modal,
  ModalBody,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Tooltip,
} from '@chakra-ui/react';
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Input from '../dealFlow/Input';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Select from 'react-select';
import { useMutation, mutateFunction } from '../../libs/apis';
import { useQueryClient } from 'react-query';
import { CustomToast } from '../toast/CustomToast';

const seedOptions = [
  'Pre-Seed Stage',
  'Seed Stage',
  'Series A',
  'Series B',
  'Series C',
  'Series D',
  'Series E',
  'Series F',
  'Pre-IPO / Mezzanine Financing',
  'IPO (Initial Public Offering)',
];

const CreateDealFlowModal = ({
  isOpen,
  onClose,
  currentPage,
  itemsPerPage,
  onComplete = () => { },
}) => {
  const { addToast } = CustomToast();
  const schema = yup.object({
    name: yup.string().required('Name is required'),
    companyId: yup.string().required('Company is required'),
    valuation: yup.string().required('Valuation is required'),
    stage: yup.string().required('Stage is required'),
    targetAmount: yup.string().required('Target amount is required'),
    minTicketSize: yup
      .string()
      .required('Minimum ticket size is required')
      .test(
        'less-than-target',
        'Minimum ticket size cannot be greater than target amount',
        function (value) {
          const targetAmount = this.parent.targetAmount;
          if (!value || !targetAmount) return true;
          return Number(value) <= Number(targetAmount);
        },
      ),
    closeDate: yup.string().required('Closing date is required'),
    data_room_link: yup.string().optional(),
    raiseDescription: yup.string().optional(),
  });

  const { mutate: createDealFlow, isLoading: mutateIsLoading } =
    useMutation(mutateFunction);

  const queryClient = useQueryClient();

  const [selectedCompany, setSelectedCompany] = useState();
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      companyId: '',
      valuation: selectedCompany?.valuation ?? '',
      stage: '',
      data_room_link: '',
      raiseDescription: '',
      targetAmount: '',
      minTicketSize: '',
      closeDate: '',
    },
    mode: 'onBlur',
  });

  const watchedSeed = watch('stage');
  const companyInfo = useSelector((state) => state?.company?.companyInfo);
  const allCompaniesInfo = useSelector(
    (state) => state?.allcompanies?.allCompaniesInfo,
  );

  const [companyOptions, setCompanyOptions] = useState([]);
  // console.log('companyinfo', companyInfo);
  // console.log('allCompaniesInfo', allCompaniesInfo);
  // console.log('selectedCompany', selectedCompany);

  useEffect(() => {
    if (watchedSeed) {
      setValue('name', watchedSeed);
    }
  }, [watchedSeed]);

  useEffect(() => {
    if (allCompaniesInfo) {
      setCompanyOptions(
        allCompaniesInfo.map((company) => ({
          value: company.id,
          label: company.title,
          valuation: company.valuation,
        })),
      );
    }
  }, [allCompaniesInfo]);

  const onCloseModal = () => {
    setValue('name', '');
    setValue('companyId', '');
    setValue('valuation', '');
    setValue('stage', '');
    setValue('targetAmount', '');
    setValue('minTicketSize', '');
    setValue('closeDate', '');
    setValue('data_room_link', '');
    setValue('raiseDescription', '');
    onClose();
  };

  const onSubmit = (data) => {
    createDealFlow(
      {
        key: 'deal-flow/create',
        method: 'post',
        data: {
          name: data.name,
          company_id: data?.companyId,
          valuation: data?.valuation,
          stage: data?.stage,
          target_amount: data?.targetAmount,
          min_ticket_size: data?.minTicketSize,
          close_date: data?.closeDate,
          ...(data?.data_room_link && { data_room_link: data?.data_room_link }),
          ...(data?.raiseDescription && {
            description: data?.raiseDescription,
          }),
        },
      },
      {
        onSuccess: (res) => {
          queryClient.invalidateQueries(
            `deal-flow?page_no=${currentPage + 1}&page_size=${itemsPerPage}`,
          );
          queryClient.invalidateQueries(`deal-flow/metrics`);
          addToast(res);
          onCloseModal();
          onComplete();
        },
        onError: (err) => {
          addToast(
            err?.response?.data?.message || 'Error creating deal flow',
            'error',
          );
        },
      },
    );
  };

  const minCloseDate = useMemo(() => {
    const today = new Date();
    today.setDate(today.getDate() + 2);
    return today.toISOString().split('T')[0];
  }, []);

  return (
    <div>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent maxH={['95vh', '90vh', '85vh', '80vh']}>
          <ModalHeader>Launch a Raise</ModalHeader>
          <ModalBody p={4} overflowY="auto" className="hide-scroll">
            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              className="w-full flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1">
                <div className="flex items-center">
                  <label>Company</label>
                  <span>*</span>
                  <Tooltip label="Select the company for which you want to create a deal flow">
                    <span className="ml-1 text-gray-500 cursor-help">ⓘ</span>
                  </Tooltip>
                </div>

                <Controller
                  name="companyId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select a company"
                      options={companyOptions}
                      styles={{
                        control: (baseStyles) => ({
                          ...baseStyles,
                          backgroundColor: 'transparent',
                          borderColor: '#00000033',
                          borderRadius: '0.25rem',
                          padding: '0.125rem',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#00000033',
                          },
                        }),
                        placeholder: (baseStyles) => ({
                          ...baseStyles,
                          color: '#17171780',
                        }),
                        input: (baseStyles) => ({
                          ...baseStyles,
                          color: 'inherit',
                        }),
                      }}
                      value={companyOptions.find(
                        (option) => option.value === field.value,
                      )}
                      onChange={(option) => {
                        field.onChange(option.value);
                        setSelectedCompany(option);
                      }}
                    />
                  )}
                />

                {errors.companyId ? (
                  <p className="flex self-start text-red-500 font-semibold mt-0.5 text-xs md:text-sm">
                    {errors.companyId.message}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1">
                <div className="flex items-center">
                  <label>Stage</label>
                  <span>*</span>
                  <Tooltip label="Select the current funding stage of the company">
                    <span className="ml-1 text-gray-500 cursor-help">ⓘ</span>
                  </Tooltip>
                </div>
                <Controller
                  name="stage"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select a seed"
                      options={seedOptions?.map((seed) => ({
                        label: seed,
                        value: seed,
                      }))}
                      styles={{
                        control: (baseStyles) => ({
                          ...baseStyles,
                          backgroundColor: 'transparent',
                          borderColor: '#00000033',
                          borderRadius: '0.25rem',
                          padding: '0.125rem',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#00000033',
                          },
                        }),
                        placeholder: (baseStyles) => ({
                          ...baseStyles,
                          color: '#17171780',
                        }),
                        input: (baseStyles) => ({
                          ...baseStyles,
                          color: 'inherit',
                        }),
                      }}
                      value={seedOptions
                        ?.map((seed) => ({ label: seed, value: seed }))
                        .find((option) => option.value === field.value)}
                      onChange={(option) => {
                        field.onChange(option.value);
                      }}
                    />
                  )}
                />

                {errors.stage ? (
                  <p className="flex self-start text-red-500 font-semibold mt-0.5 text-xs md:text-sm">
                    {errors.stage.message}
                  </p>
                ) : null}
              </div>

              <Input
                label="Raise Name"
                placeholder="Deal Flow Name"
                type="text"
                htmlFor="name"
                error={errors.name?.message}
                required
                tooltip="Enter a name to identify this fundraising round (e.g., Seed Round, Series A)."
                {...register('name')}
              />

              <Input
                label="Current Valuation"
                placeholder="$10,000,000"
                type="number"
                htmlFor="valuation"
                error={errors.valuation?.message}
                required
                tooltip="Your company’s current estimated valuation before this raise."
                {...register('valuation')}
              />

              <Input
                label="Target Amount to Raise"
                placeholder="$2,000,000"
                type="number"
                htmlFor="tragetAmount"
                error={errors.targetAmount?.message}
                required
                tooltip="The total amount you aim to raise in this round."
                {...register('targetAmount')}
              />

              <Input
                label="Minimum Investment Ticket Size"
                placeholder="$25,000"
                type="number"
                htmlFor="minTicketSize"
                error={errors.minTicketSize?.message}
                required
                tooltip="The smallest amount an individual investor is allowed to commit."
                {...register('minTicketSize')}
              />

              <Input
                label="Fundraising Close Date"
                type="date"
                htmlFor="closeDate"
                error={errors.closeDate?.message}
                required
                min={minCloseDate}
                tooltip="Expected closing date of this raise. You can update this later if needed."
                {...register('closeDate')}
              />

              <Input
                label="Raise Description"
                placeholder="e.g., 'This round will help us expand into new markets...'"
                type="textarea"
                htmlFor="raiseDescription"
                error={errors.raiseDescription?.message}
                isTextArea
                tooltip="Provide a short summary of the purpose and goals of this raise to inform potential investors."
                {...register('raiseDescription')}
              />

              <Input
                label="Data Room Link"
                placeholder="https://www.google.com"
                type="text"
                htmlFor="data_room_link"
                error={errors.data_room_link?.message}
                {...register('data_room_link')}
              />

              <button
                type="submit"
                className="bg-[#134A70] text-white px-4 py-2 rounded w-fit"
              >
                {mutateIsLoading ? 'Creating...' : 'Create'}
              </button>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default CreateDealFlowModal;
